// web-frontend/src/shared/services/apiClient.js - 웹용 (fetch 기반, WebSocket 지원)
// 🔥 수정: TOKEN_USER_NOT_FOUND 에러 시 즉시 로그아웃 처리
// 🔥🔥🔥 [2025-12-19 수정] createStandardError 보강 + template literal 문법 오류 수정
// 🔥🔥🔥 [2026-01-15 수정] console.log 및 fetch 템플릿 리터럴 구문 오류 전체 수정
import { getStoredToken, removeStoredToken, clearAuthData } from '../utils/storage';
import logger from '../utils/logger';

// ==================== 🔥 [핵심 수정] URL 설정 로직 강화 - Mixed Content 방지 ====================
const getApiUrl = () => {
  // 1. 브라우저 환경 확인
  if (typeof window !== 'undefined') {
    const { protocol, hostname, origin } = window.location;

    // 🔥 [강제] ngrok, 로컬 네트워크, 또는 HTTPS 환경인 경우
    // 환경변수보다 현재 브라우저의 주소(Origin)를 최우선으로 사용합니다.
    // 이렇게 하면 Nginx 프록시(/api)를 통해 안전하게 요청이 전달됩니다.
    if (
      hostname.includes('ngrok') ||
      hostname.includes('netzero-sapiens') ||
      protocol === 'https:'
    ) {
      logger.debug(`🔒 [Security] Enforcing API URL to match origin: ${origin}/api`);
      return `${origin}/api`;
    }
  }

  // 2. 로컬 개발 환경 (http://localhost)일 때만 환경변수 사용
  const envApiUrl = import.meta.env.VITE_API_URL;
  if (envApiUrl) {
    logger.debug('✅ Using API URL from environment:', envApiUrl);
    // 🔥 마지막 슬래시 제거
    return envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl;
  }

  // 3. Fallback - 개발 환경 기본값
  const defaultUrl = 'http://localhost:8000/api';
  logger.warn('⚠️ Using default API URL:', defaultUrl);
  return defaultUrl;
};

// ==================== ApiClient 클래스 ====================
class ApiClient {
  constructor() {
    this.baseURL = getApiUrl();
    // 🔥 [수정] 타임아웃을 30초 -> 60초로 증가 (AI 매칭 등 긴 작업 대비)
    this.timeout = 60000;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.isInitialized = false;
    this.token = null;
    this.deviceId = null;

    // 🔥 인증 에러 핸들러 (외부에서 설정 가능)
    this.onAuthenticationError = null;

    // 토큰 갱신 상태 관리 (동시 갱신 방지)
    this._isRefreshing = false;
    this._refreshPromise = null;
    this.refreshTimeout = 10000; // refresh 전용 타임아웃 10초

    this.guestAllowedEndpoints = [
      '/stats/dashboard',
      '/companies',
      '/companies/recommended',
      '/companies/featured',
    ];

    logger.debug('🚀 ApiClient 생성됨 - baseURL:', this.baseURL);
  }

  // ==================== 🔥🔥🔥 WebSocket URL 생성 헬퍼 ====================
  /**
   * WebSocket URL을 생성합니다.
   * - http:// → ws://
   * - https:// → wss://
   * - 상대 경로인 경우 현재 프로토콜 기준으로 자동 변환
   *
   * @param {string} endpoint - WebSocket 엔드포인트 (예: '/chats/ws/...')
   * @returns {string} WebSocket URL (예: 'wss://example.com/api/chats/ws/...')
   */
  getWebSocketUrl(endpoint) {
    let wsUrl = this.baseURL;

    // http -> ws, https -> wss 변환
    if (wsUrl.startsWith('https://')) {
      wsUrl = wsUrl.replace('https://', 'wss://');
      logger.debug('🔒 HTTPS 감지 → WSS 프로토콜 사용');
    } else if (wsUrl.startsWith('http://')) {
      wsUrl = wsUrl.replace('http://', 'ws://');
      logger.debug('🔓 HTTP 감지 → WS 프로토콜 사용');
    } else if (wsUrl.startsWith('/')) {
      // 상대 경로인 경우 현재 프로토콜 확인
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}${wsUrl}`;
      logger.debug(`🌐 상대 경로 감지 → ${protocol} 프로토콜 사용`);
    }

    // 엔드포인트 결합 (슬래시 처리)
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const finalUrl = `${wsUrl}${cleanEndpoint}`;

    logger.debug('🔌 WebSocket URL 생성:', finalUrl);
    return finalUrl;
  }

  // ==================== 초기화 ====================
  async initialize() {
    try {
      logger.debug('🔄 API 클라이언트 초기화 시작...');

      try {
        this.token = await getStoredToken();
        if (this.token) {
          logger.debug('✅ 토큰 로드 완료');
        } else {
          logger.debug('ℹ️ 저장된 토큰 없음');
        }
      } catch (error) {
        logger.warn('⚠️ 토큰 로드 실패:', error);
        this.token = null;
      }

      // 🔥 웹 환경에서는 브라우저 fingerprint를 deviceId로 사용
      try {
        this.deviceId = this.generateDeviceId();
        logger.debug('✅ 디바이스 ID 생성 완료:', this.deviceId);
      } catch (error) {
        logger.warn('⚠️ 디바이스 ID 생성 실패:', error);
        this.deviceId = null;
      }

      this.isInitialized = true;
      logger.debug('✅ API 클라이언트 초기화 완료');
    } catch (error) {
      logger.error('❌ API 클라이언트 초기화 실패:', error);
      this.isInitialized = true;
    }
  }

  // 🔥 웹용 디바이스 ID 생성 (브라우저 fingerprint)
  generateDeviceId() {
    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const platform = navigator.platform;
    const screenResolution = `${window.screen.width}x${window.screen.height}`;

    const fingerprint = `${userAgent}-${language}-${platform}-${screenResolution}`;

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    return `web-${Math.abs(hash).toString(36)}`;
  }

  // 🔥 인증 에러 핸들러 설정
  setAuthErrorHandler(handler) {
    this.onAuthenticationError = handler;
    logger.debug('🔧 인증 에러 핸들러 설정됨');
  }

  // 🔥 토큰 설정
  setToken(token) {
    const oldToken = this.token;
    this.token = token;
    // localStorage에도 저장
    if (token) {
      localStorage.setItem('access_token', token);
    }
    logger.debug('🔑 [apiClient] 토큰 설정 완료');
    logger.debug('   - 이전 토큰:', oldToken ? oldToken.substring(0, 20) + '...' : 'null');
    logger.debug('   - 새 토큰:', token ? token.substring(0, 20) + '...' : 'null');
  }

  // 🔥 토큰 제거
  clearToken() {
    const oldToken = this.token;
    this.token = null;
    // localStorage에서도 제거
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    logger.debug('🔓 [apiClient] 토큰 제거 완료');
    logger.debug('   - 이전 토큰:', oldToken ? oldToken.substring(0, 20) + '...' : 'null');
    logger.debug('   - 새 토큰:', null);
  }

  // ==================== 헤더 관리 ====================
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Client-Platform': 'web',
      'X-Client-Version': '1.0.0',
      // 🔥 [핵심 수정] Ngrok 무료 버전 경고 페이지 우회 헤더 추가
      'ngrok-skip-browser-warning': '69420',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.deviceId) {
      headers['X-Device-Id'] = this.deviceId;
    }

    return headers;
  }

  // ==================== 유틸리티 ====================
  isGuestAllowedEndpoint(url) {
    return this.guestAllowedEndpoints.some((endpoint) => url.includes(endpoint));
  }

  sanitizeHeaders(headers) {
    const sanitized = { ...headers };
    if (sanitized.Authorization) {
      sanitized.Authorization = 'Bearer [REDACTED]';
    }
    return sanitized;
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  shouldRetry(error) {
    // 🔥 [수정] AbortError는 재시도하지 않음
    if (error.name === 'AbortError') return false;
    if (error.code === 'NETWORK_ERROR') return true;
    if (error.message?.includes('Network')) return true;
    if (error.response) return false;
    return true;
  }

  // ==================== 🔥🔥🔥 [핵심 수정] 토큰 관련 에러인지 확인 ====================
  /**
   * 토큰이 무효화되어 재발급이 불가능한 에러인지 확인
   * - TOKEN_USER_NOT_FOUND: DB에 사용자가 없음 (토큰 갱신 무의미)
   * - USER_INACTIVE: 비활성화된 사용자
   */
  isTokenInvalidError(errorData) {
    if (!errorData) return false;
    
    // detail이 객체인 경우 (새로운 형식)
    if (typeof errorData === 'object' && errorData.code) {
      const invalidCodes = ['TOKEN_USER_NOT_FOUND', 'USER_INACTIVE'];
      return invalidCodes.includes(errorData.code);
    }
    
    // detail이 문자열인 경우 (기존 형식 호환)
    if (typeof errorData === 'string') {
      return errorData.includes('User not found') || 
             errorData.includes('Inactive user');
    }
    
    // detail.message가 있는 경우
    if (errorData.message) {
      return errorData.message.includes('User not found') ||
             errorData.message.includes('Inactive user');
    }
    
    return false;
  }

  // ==================== 🔥 토큰 갱신 (동시 호출 방지 + 전용 타임아웃) ====================
  async refreshAccessToken() {
    // 이미 갱신 중이면 기존 Promise 재사용 (동시 401 다중 갱신 방지)
    if (this._isRefreshing && this._refreshPromise) {
      return this._refreshPromise;
    }

    this._isRefreshing = true;
    this._refreshPromise = this._doRefresh();

    try {
      return await this._refreshPromise;
    } finally {
      this._isRefreshing = false;
      this._refreshPromise = null;
    }
  }

  async _doRefresh() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return null;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.refreshTimeout);

    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': '69420',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('토큰 갱신 실패');
      }

      const data = await response.json();
      const { access_token, refresh_token: newRefreshToken } = data;

      if (access_token) {
        this.setToken(access_token);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }
        return access_token;
      }

      return null;
    } catch (error) {
      clearTimeout(timeoutId);
      return null;
    }
  }

  // ==================== 🔥 HTTP 요청 (디버깅 로그 통합 + 안전한 응답 처리) ====================
  async request(config) {
    // 🔥 [수정] 외부 signal 지원을 위해 내부 controller와 연결
    const controller = new AbortController();

    // 외부에서 signal이 들어오면 내부 controller도 함께 abort 시킴
    if (config.signal) {
      config.signal.addEventListener('abort', () => {
        controller.abort();
      });
    }

    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    const requestId = Math.random().toString(36).substring(7);
    const startTime = Date.now();

    try {
      // 토큰 로드
      let token = this.token;
      if (!token) {
        try {
          token = await getStoredToken();
          if (token) {
            this.token = token;
            logger.debug(
              '🔄 [apiClient] localStorage에서 토큰 로드:',
              token.substring(0, 20) + '...'
            );
          }
        } catch (error) {
          logger.warn('토큰 가져오기 실패:', error);
        }
      }

      // URL 구성
      const url = config.url.startsWith('http') ? config.url : `${this.baseURL}${config.url}`;

      // 🔥 [수정] getHeaders() 호출하여 ngrok 헤더 포함된 헤더 가져오기
      const headers = {
        ...this.getHeaders(),
        ...config.headers,
      };

      // Fetch 옵션 구성
      const fetchOptions = {
        method: config.method || 'GET',
        headers,
        signal: controller.signal, // 내부 controller signal 사용
      };

      // Body 처리
      if (config.data && fetchOptions.method !== 'GET') {
        if (config.data instanceof FormData) {
          fetchOptions.body = config.data;
          delete fetchOptions.headers['Content-Type'];
        } else {
          fetchOptions.body = JSON.stringify(config.data);
        }
      }

      // Query Parameters 처리
      let finalUrl = url;
      if (config.params && fetchOptions.method === 'GET') {
        const searchParams = new URLSearchParams();
        Object.entries(config.params).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
        const paramString = searchParams.toString();
        if (paramString) {
          finalUrl = `${url}${url.includes('?') ? '&' : '?'}${paramString}`;
        }
      }

      // 🔥 요청 로깅
      const isMeetingRequest = finalUrl.includes('/meetings');
      if (isMeetingRequest) {
        logger.debug('='.repeat(100));
        logger.debug(`🚀 [API REQUEST] ID: ${requestId}`);
        logger.debug(`   📡 Method: ${fetchOptions.method}`);
        logger.debug(`   🔗 URL: ${finalUrl}`);
        logger.debug(`   ⏰ Time: ${new Date().toISOString()}`);
      } else if (import.meta.env.DEV) {
        logger.debug(`🚀 [API Request] ${fetchOptions.method} ${finalUrl}`, {
          params: config.params,
          data: config.data,
        });
      }

      // Fetch 실행
      const response = await fetch(finalUrl, fetchOptions);
      clearTimeout(timeoutId);

      // ==================== 🔥🔥🔥 [수정] 안전한 응답 처리 로직 시작 ====================
      // 1. 204 No Content (삭제 성공 등)는 즉시 null 반환
      if (response.status === 204) {
        return null;
      }

      let data;
      const contentType = response.headers.get('content-type');

      // 2. JSON 응답인 경우에만 파싱 시도
      if (contentType && contentType.includes('application/json')) {
        try {
          const text = await response.text();
          // 본문이 있으면 파싱, 없으면 null
          data = text ? JSON.parse(text) : null;
        } catch (e) {
          logger.warn('⚠️ JSON 파싱 실패 (비표준 응답):', e);
          data = null;
        }
      } else {
        // 텍스트나 HTML 등 다른 형식이면 null 처리 (또는 필요시 text로 읽음)
        data = null;
      }

      const duration = Date.now() - startTime;

      // 🔥 응답 로깅
      if (isMeetingRequest) {
        const statusEmoji = response.ok ? '✅' : '❌';
        logger.debug('='.repeat(100));
        logger.debug(`${statusEmoji} [API RESPONSE] ID: ${requestId}`);
        logger.debug(`   📊 Status: ${response.status} ${response.statusText}`);
        logger.debug(`   ⏱️  Duration: ${duration}ms`);
        logger.debug('='.repeat(100));
      } else if (import.meta.env.DEV) {
        logger.debug(`✅ [API Response] ${fetchOptions.method} ${finalUrl}`, {
          status: response.status,
          duration: `${duration}ms`,
          data: data,
        });
      }

      // 3. 에러 핸들링
      if (!response.ok) {
        // 404인데 DELETE 요청이면 => 이미 삭제된 것으로 간주하고 성공 처리
        if (response.status === 404 && fetchOptions.method === 'DELETE') {
          logger.warn(`ℹ️ 리소스가 이미 삭제됨 (404 Ignored): ${finalUrl}`);
          return null;
        }

        // 🔥🔥🔥 [핵심 수정] 401 에러 처리 강화
        if (response.status === 401) {
          // 🔥🔥🔥 [2026-02-25 수정] 인증 불필요 엔드포인트는 토큰 갱신 건너뛰기
          // 로그인, 회원가입, 비밀번호 재설정 등의 401은 "자격증명 오류"이므로
          // 토큰 갱신/로그아웃 처리를 하면 안 됨
          const authFreeEndpoints = [
            '/auth/login',
            '/auth/register',
            '/auth/guest-login',
            '/auth/password-reset/',
            '/auth/check-email-exists',
            // 🔥 [FABLE B-20] /auth/refresh 자신의 401 은 갱신 재시도 없이 그대로 전달
            // (호출부인 authStore.refreshAccessToken 이 catch 후 logout 처리)
            '/auth/refresh',
          ];
          const isAuthFreeRequest = authFreeEndpoints.some(ep => config.url?.includes(ep));

          if (isAuthFreeRequest) {
            // 로그인 실패 등 → 백엔드 에러 메시지를 그대로 전달
            const error = new Error(
              data?.detail || data?.message || '이메일 또는 비밀번호가 올바르지 않습니다.'
            );
            error.status = 401;
            error.response = { data, status: response.status, statusText: response.statusText, headers: response.headers };
            error.config = config;
            throw error;
          }

          // 🔥 토큰 무효화 에러인지 확인 (User not found, Inactive user 등)
          const errorDetail = data?.detail;
          
          if (this.isTokenInvalidError(errorDetail)) {
            // 🔥🔥🔥 토큰 갱신 시도 없이 즉시 로그아웃 처리
            logger.error('🚨 [AUTH] 토큰이 무효화됨 - 즉시 로그아웃 처리');
            logger.error('   - 에러 코드:', errorDetail?.code || 'N/A');
            logger.error('   - 에러 메시지:', errorDetail?.message || errorDetail);
            
            await this.handleAuthenticationError();
            
            const error = new Error(
              errorDetail?.message || 
              '세션이 만료되었습니다. 다시 로그인해주세요.'
            );
            error.code = errorDetail?.code || 'TOKEN_INVALID';
            error.response = {
              data,
              status: response.status,
              statusText: response.statusText,
              headers: response.headers,
            };
            error.config = config;
            throw error;
          }
          
          // 🔥 일반적인 401 에러 (토큰 만료 등) - 토큰 갱신 시도
          if (!config._retry) {
            logger.warn('⚠️ 401 인증 실패 - 토큰 갱신 시도');
            const newToken = await this.refreshAccessToken();
            if (newToken) {
              // 토큰 갱신 성공 시 원래 요청 재시도
              config._retry = true;
              return this.request(config);
            }
            // 토큰 갱신 실패 시 로그아웃 처리
            await this.handleAuthenticationError();
            const error = new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
            error.response = {
              data,
              status: response.status,
              statusText: response.statusText,
              headers: response.headers,
            };
            error.config = config;
            throw error;
          }
        }

        // 🔥 403 에러 처리
        if (response.status === 403) {
          if (data?.detail?.code === 'PASSWORD_CHANGE_REQUIRED' && typeof window !== 'undefined' &&
              window.location.pathname !== '/change-password') {
            window.location.assign('/change-password');
          }
          logger.error('권한이 없습니다.');
        }

        // 🔥 404 에러는 throw하지 않고 반환 (DELETE 제외)
        if (response.status === 404) {
          logger.warn(`⚠️ 404 Not Found: ${finalUrl}`);
          return {
            data: data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            isError: true,
          };
        }

        // 🔥 500 에러 처리
        if (response.status >= 500) {
          logger.error('서버 오류가 발생했습니다.');
        }

        // 기타 에러는 throw
        const error = new Error(data?.message || data?.detail?.message || `HTTP Error ${response.status}`);
        error.status = response.status;
        error.data = data;
        error.response = {
          data,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        };
        error.config = config;
        throw error;
      }

      // ==================== 🔥 [수정] 안전한 응답 처리 로직 끝 ====================

      this.retryCount = 0;
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;

      // 🔥 [수정] AbortError(요청 취소)는 에러 로그 출력 및 재시도 하지 않음
      if (error.name === 'AbortError') {
        if (import.meta.env.DEV) {
          logger.debug(`✋ [API Aborted] 요청이 취소되었습니다: ${config.url}`);
        }
        throw error; // 재시도 로직 타지 않게 바로 throw
      }

      // 네트워크 에러 처리
      if (!error.response) {
        logger.error('네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.');
      }

      if (import.meta.env.DEV) {
        logger.error(`❌ [API Error] ${config.method?.toUpperCase()} ${config.url}`, {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data,
          duration: `${duration}ms`,
        });
      }

      // 재시도 로직
      if (this.shouldRetry(error) && this.retryCount < this.maxRetries) {
        this.retryCount++;
        logger.debug(`🔄 재시도 ${this.retryCount}/${this.maxRetries}...`);
        await this.delay(1000 * this.retryCount);
        return this.request(config);
      }

      this.retryCount = 0;
      const standardError = this.createStandardError(error);
      throw standardError;
    }
  }

  // ==================== 🔥 에러 처리 강화 ====================
  async handleAuthenticationError() {
    try {
      logger.debug('🚪 인증 에러 처리 시작');

      // 1. 로컬 데이터 클리어
      await clearAuthData();
      this.clearToken();
      logger.debug('✅ 인증 정보 클리어 완료');

      // 2. 외부 핸들러 호출
      if (this.onAuthenticationError) {
        logger.debug('📞 외부 인증 에러 핸들러 호출');
        this.onAuthenticationError();
      }

      // 🔥 [수정] 로그인 페이지로 리다이렉트 (외부 핸들러가 없을 경우에만)
      if (typeof window !== 'undefined' && !this.onAuthenticationError) {
        logger.debug('🔄 로그인 페이지로 리다이렉트');
        // 현재 페이지가 로그인 페이지가 아닌 경우에만 리다이렉트
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    } catch (error) {
      logger.error('❌ 인증 정보 클리어 실패:', error);
    }
  }

  // 🔥🔥🔥 [2025-12-19 수정] createStandardError 메서드 보강
  createStandardError(error) {
    const standardError = new Error();
    
    // 🔥 [추가] 원본 에러 정보 보존
    standardError.originalError = error;

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      standardError.status = status;
      standardError.response = error.response; // 🔥 response 객체 연결
      standardError.config = error.config;
      standardError.code = error.code || data?.detail?.code;

      switch (status) {
        case 400:
          standardError.message = data?.message || data?.detail || '잘못된 요청입니다.';
          break;
        case 401:
          // 🔥 [수정] 에러 코드에 따른 메시지 분기
          if (data?.detail?.code === 'TOKEN_USER_NOT_FOUND') {
            standardError.message = '세션이 만료되었습니다. 다시 로그인해주세요.';
          } else if (data?.detail?.code === 'USER_INACTIVE') {
            standardError.message = '비활성화된 계정입니다. 관리자에게 문의하세요.';
          } else {
            standardError.message = '인증이 필요합니다. 다시 로그인해주세요.';
          }
          break;
        case 403:
          standardError.message = '권한이 없습니다.';
          break;
        case 404:
          standardError.message = '요청한 정보를 찾을 수 없습니다.';
          break;
        case 500:
          standardError.message = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
          break;
        default:
          standardError.message =
            data?.message || data?.detail || `알 수 없는 오류가 발생했습니다. (${status})`;
      }
    } else if (error.name === 'AbortError') {
      standardError.message = '요청 시간이 초과되었습니다. 네트워크 연결을 확인해주세요.';
      standardError.code = 'TIMEOUT';
    } else {
      // 🔥🔥🔥 [핵심 수정] Network Error 등 response가 없는 경우 처리 강화
      standardError.message = error.message || '네트워크 연결을 확인해주세요.';
      standardError.code = error.code || 'NETWORK_ERROR';
      // 🔥 [추가] 안전한 접근을 위해 빈 response 객체 설정 (옵셔널 체이닝 대비)
      // standardError.response = {}; // 필요시 주석 해제
    }

    return standardError;
  }

  // ==================== HTTP 메서드 ====================
  get(url, configOrParams = {}) {
    let config;
    if (configOrParams.params || configOrParams.headers || configOrParams.timeout) {
      config = configOrParams;
    } else if (Object.keys(configOrParams).length > 0) {
      config = { params: configOrParams };
    } else {
      config = {};
    }
    return this.request({ method: 'GET', url, ...config });
  }

  post(url, data, config = {}) {
    return this.request({ method: 'POST', url, data, ...config });
  }

  put(url, data, config = {}) {
    return this.request({ method: 'PUT', url, data, ...config });
  }

  patch(url, data, config = {}) {
    return this.request({ method: 'PATCH', url, data, ...config });
  }

  delete(url, config = {}) {
    return this.request({ method: 'DELETE', url, ...config });
  }

  // ==================== 파일 업로드 ====================
  async uploadFile(url, file, config = {}) {
    const formData = new FormData();
    formData.append('file', file);

    return this.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        // Content-Type은 FormData가 자동 설정
      },
    });
  }
}

// ==================== 싱글톤 인스턴스 ====================
const apiClient = new ApiClient();

// ==================== 🔥 헬퍼 함수 ====================
/**
 * 인증 토큰 설정
 * @param {string|null} token - 액세스 토큰
 */
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('access_token', token);
    apiClient.setToken(token);
  } else {
    localStorage.removeItem('access_token');
    apiClient.clearToken();
  }
};

/**
 * 모든 인증 토큰 제거
 */
export const clearAuthTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  apiClient.clearToken();
};

/**
 * 저장된 인증 토큰 가져오기
 * @returns {string|null} 액세스 토큰
 */
export const getAuthToken = () => {
  return localStorage.getItem('access_token');
};

/**
 * 인증 여부 확인
 * @returns {boolean} 인증 여부
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('access_token');
};

/**
 * 리프레시 토큰 설정
 * @param {string} token - 리프레시 토큰
 */
export const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem('refresh_token', token);
  } else {
    localStorage.removeItem('refresh_token');
  }
};

/**
 * 리프레시 토큰 가져오기
 * @returns {string|null} 리프레시 토큰
 */
export const getRefreshToken = () => {
  return localStorage.getItem('refresh_token');
};

// 개발 환경에서 정보 출력
if (import.meta.env.DEV) {
  setTimeout(() => {
    logger.debug('✅ ApiClient loaded - baseURL:', apiClient.baseURL);
  }, 500);
}

export { apiClient };
export default apiClient;
